"use client";

import { useEffect } from "react";
import { useHreflang } from "@/i18n/useHreflang";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import ArticleHeader from "./components/ArticleHeader";
import CodeStamp from "./components/CodeStamp";
import IntroParagraph from "./components/IntroParagraph";
import GlossaryBox from "./components/GlossaryBox";
import FloridaZonesMap from "./components/FloridaZonesMap";
import CalloutBox from "./components/CalloutBox";
import NoaWorkflow from "./components/NoaWorkflow";
import StandardsTable from "./components/StandardsTable";
import HomeownerCards from "./components/HomeownerCards";
import InspectorFinder from "./components/InspectorFinder";
import RelatedArticles from "./components/RelatedArticles";
import ArticleCta from "./components/ArticleCta";
import { injectMetaTags } from "@/lib/seoHelpers";

export default function BlogCodesArticle() {
  useHreflang();
  const { t, currentLang } = useLanguage();
  const isEs = currentLang === "es";

  useEffect(() => {
    document.title = t("blog.codes.seoTitle") as string;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", t("blog.codes.seoDesc") as string);

    const baseUrl = "https://impact-window-repair-miami.com";
    const path = isEs ? "/es/blog/codigo-hvhz-noa-explicado/" : "/blog/hvhz-code-noa-explained/";
    const url = `${baseUrl}${path}`;

    const cleanupSeo = injectMetaTags({
      title: t("blog.codes.seoTitle") as string,
      description: t("blog.codes.seoDesc") as string,
      url,
      image: "https://impact-window-repair-miami.com/og/og-blog-hvhz.jpg",
      imageAlt: t("blog.codes.imgAlt") as string,
      breadcrumbs: [
        { name: isEs ? "Inicio" : "Home", item: baseUrl + "/" },
        { name: "Blog", item: baseUrl + "/blog/" },
        { name: t("blog.codes.h1") as string, item: url }
      ],
      type: "article",
      articleAuthor: "Windows and Doors Repair LLC",
      articlePublisher: baseUrl,
      articleSection: isEs ? "Información de Construcción" : "Window Repair Information",
      articlePublishedTime: "2026-05-18",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: t("blog.codes.h1"),
      inLanguage: isEs ? "es-US" : "en-US",
      author: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      datePublished: "2026-05-18",
      dateModified: "2026-05-18",
      publisher: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      description: t("blog.codes.seoDesc"),
    };

    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "blogpost-schema";
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);

    return () => {
      cleanupSeo();
      const el = document.getElementById("blogpost-schema");
      if (el) document.head.removeChild(el);
    };
  }, [currentLang, t, isEs]);

  return (
    <main className="bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <ArticleHeader />

        <div className="bg-[#ea580c]/10 border-l-4 border-[#ea580c] rounded-r-xl p-6 mb-10">
          <p className="text-[#064e3b] font-semibold text-sm leading-relaxed">
            {t("blog.codes.disclaimer")}
            {" "}
            <Link to={isEs ? "/es/reemplazo-de-vidrios/" : "/glass-replacement/"} className="text-[#047857] underline">
              {isEs ? "servicio de reemplazo de vidrios" : "glass replacement service"}
            </Link>{" "}
            {isEs ? "o" : "or"}{" "}
            <Link to={isEs ? "/es/tapiado-de-emergencia/" : "/emergency-board-up/"} className="text-[#047857] underline">
              {isEs ? "tapiado de emergencia" : "emergency board-up"}
            </Link>.
          </p>
        </div>

        <CodeStamp />
        <IntroParagraph />
        <GlossaryBox />
        <FloridaZonesMap />
        <CalloutBox />
        <NoaWorkflow />
        <StandardsTable />
        <HomeownerCards />
        <InspectorFinder />
        <RelatedArticles />
      </div>
      <ArticleCta />
    </main>
  );
}